import React, { useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Store, Zap } from 'lucide-react';

export default function CreateVendorDialog({ open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    business_type: 'Restaurant',
    address: '',
    quick_create: true
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Email, password, and business name are required');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams(formData);
      await api.post(`/admin/vendors/create?${params}`);
      toast.success(`Vendor created successfully!`);
      setFormData({ email: '', password: '', name: '', phone: '', business_type: 'Restaurant', address: '', quick_create: true });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create vendor');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Vendor</DialogTitle>
          <DialogDescription>
            Add a new restaurant or store to the platform
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Alert className={formData.quick_create ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}>
            <div className="flex items-center gap-2">
              {formData.quick_create ? <Zap className="h-4 w-4 text-blue-600" /> : <Store className="h-4 w-4 text-emerald-600" />}
              <AlertDescription className="text-sm">
                {formData.quick_create 
                  ? <span className="text-blue-900"><strong>Quick Create:</strong> Vendor will complete 7-step onboarding wizard</span>
                  : <span className="text-emerald-900"><strong>Full Create:</strong> Vendor account ready to use immediately</span>
                }
              </AlertDescription>
            </div>
          </Alert>

          <div className="space-y-2">
            <Label>Creation Type</Label>
            <Select value={formData.quick_create ? 'quick' : 'full'} onValueChange={value => setFormData({...formData, quick_create: value === 'quick'})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="quick">Quick Create (Vendor completes onboarding)</SelectItem>
                <SelectItem value="full">Full Create (Admin sets all)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business Name *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="Mario's Pizza"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Business Type *</Label>
              <Select value={formData.business_type} onValueChange={value => setFormData({...formData, business_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Restaurant">Restaurant</SelectItem>
                  <SelectItem value="Fast Food">Fast Food</SelectItem>
                  <SelectItem value="Cafe">Cafe</SelectItem>
                  <SelectItem value="Bakery">Bakery</SelectItem>
                  <SelectItem value="Grocery Store">Grocery Store</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              placeholder="vendor@example.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Password *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder="Min 8 characters"
              minLength={8}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                placeholder="+1-555-0200"
              />
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={e => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main St"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Vendor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
